// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useEffect } from 'react';
import { useSessionGuardStore } from '../stores/sessionGuardStore';

/**
 * Real user input only: click, keydown, touchstart. Deliberately excludes
 * `mousemove`/`scroll` — those fire on passive cursor drift or reading a long
 * page, and counting them would make the idle timer meaningless (ADO-25798:
 * "only real input... resets the idle timer — background polling or passive
 * screen activity doesn't count").
 */
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ['click', 'keydown', 'touchstart'];

/**
 * Attaches window-level activity listeners that bump `lastActivityAt`. No
 * listeners at all when `enabled` is false (kiosk mode, or not
 * authenticated) — this is what makes the wall-display exemption a true
 * no-op rather than a guard that runs and immediately bails.
 */
export function useIdleActivityTracker(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return undefined;

    const onActivity = () => {
      const { warningVisible, locked } = useSessionGuardStore.getState();
      // Once the warning or lock UI is up, ambient window-level clicks (e.g.
      // one that lands on the overlay's own backdrop) must not silently
      // extend the session — only the overlay's own explicit CTA does that.
      // Without this, the capture-phase listener below fires before the
      // click ever reaches the overlay's DOM, so it looks indistinguishable
      // from "clicking outside dismissed it."
      if (warningVisible || locked) return;
      useSessionGuardStore.getState().recordActivity();
    };
    ACTIVITY_EVENTS.forEach(type =>
      window.addEventListener(type, onActivity, { capture: true, passive: true })
    );
    return () => {
      ACTIVITY_EVENTS.forEach(type =>
        window.removeEventListener(type, onActivity, { capture: true })
      );
    };
  }, [enabled]);
}
